import { Inject, Injectable, inject } from "@angular/core";
import { DemoParticleCollectionRepository } from "@partikelart/particles/data-access-demo";
import {
	Material,
	Particle,
	type ParticleCollection,
	type ParticleMeasurements,
	Point2D,
	type SizeClassBin,
	toSizeClass,
	WayOfMeasurement,
} from "@partikelart/particles/domain";
import { SPECIFICATION_REPOSITORY, type SpecificationRepository } from "@partikelart/specification/data-access-core";
import { type Specification, VDA19_SIZE_CLASSES } from "@partikelart/specification/domain";
import { type SurfaceReportRepository } from "@partikelart/surface/data-access-core";
import {
	createSizeClassBinFromSurfaceReport,
	type SurfaceReport,
	type SurfaceReportSummary,
	type SurfaceReportWithSpecification,
} from "@partikelart/surface/domain";
import type { CreateSurfaceReportDto, UpdateSurfaceReportDto } from "@partikelart/surface/dtos";
import { Observable, of, throwError } from "rxjs";
import { catchError, delay, map } from "rxjs/operators";

/**
 * Safely resolve the SPECIFICATION_REPOSITORY from the current Angular DI
 * context. Returns null when invoked outside an injection context (e.g. in
 * unit tests that construct the repository with `new`), so the demo repository
 * degrades gracefully to "no specification joined" instead of throwing.
 */
function safeInjectSpec(): SpecificationRepository | null {
	try {
		return inject(SPECIFICATION_REPOSITORY, { optional: true });
	} catch {
		return null;
	}
}

const MICRONS_PER_PIXEL = 5;

// ── Cross-reference IDs (shared with DemoPartRepository & DemoSpecificationRepository) ──
const PART_ALPHA = "00000000-0000-4000-8000-000000000001";
const PART_BETA = "00000000-0000-4000-8000-000000000002";
const PART_GAMMA = "00000000-0000-4000-8000-000000000003";
const PART_DELTA = "00000000-0000-4000-8000-000000000004";
const PART_EPSILON = "00000000-0000-4000-8000-000000000005";
const PART_ZETA = "00000000-0000-4000-8000-000000000006";

const SPEC_ALPHA = "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d";
const SPEC_BETA = "c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f";
const SPEC_DELTA = "d0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a";

const SITE_1 = "d4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a";
const SITE_2 = "e1f2a3b4-c5d6-4e7f-8a9b-0c1d2e3f4a5b";

@Injectable({ providedIn: "root" })
export class DemoSurfaceReportRepository implements SurfaceReportRepository {
	private readonly latencyMs = 200;
	private readonly explicitSpecRepo: SpecificationRepository | null;
	private readonly diSpecRepo: SpecificationRepository | null;
	private reports: SurfaceReport[] = [];

	constructor(@Inject(SPECIFICATION_REPOSITORY) specRepo?: SpecificationRepository) {
		// Prefer an explicitly-passed repository (used by tests); otherwise try
		// to resolve SPECIFICATION_REPOSITORY from the Angular DI context. The
		// optional inject() returns null when this class is constructed outside
		// Angular (e.g. in unit tests), preserving the previous demo behaviour.
		this.explicitSpecRepo = specRepo ?? null;
		this.diSpecRepo = safeInjectSpec();
		this.reports = this.buildSeedReports();
		for (const r of this.reports) {
			r.sizeClassEntries = this.buildSizeClassEntries(r);
		}
	}

	private get specRepo(): SpecificationRepository | null {
		return this.explicitSpecRepo ?? this.diSpecRepo;
	}

	getAll(params?: {
		siteId?: string;
		partId?: string;
		dateFrom?: string;
		dateTo?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ data: SurfaceReport[]; total: number }> {
		let filtered = [...this.reports];
		if (params?.siteId) filtered = filtered.filter((r) => r.siteId === params.siteId);
		if (params?.partId) filtered = filtered.filter((r) => r.partId === params.partId);
		const total = filtered.length;
		const offset = params?.offset ?? 0;
		const limit = params?.limit ?? total;
		const data = filtered.slice(offset, offset + limit);
		return of({ data, total }).pipe(delay(this.latencyMs));
	}

	getSummaries(params?: {
		siteId?: string;
		partId?: string;
		dateFrom?: string;
		dateTo?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ data: SurfaceReportSummary[]; total: number }> {
		let filtered = [...this.reports];
		if (params?.siteId) filtered = filtered.filter((r) => r.siteId === params.siteId);
		if (params?.partId) filtered = filtered.filter((r) => r.partId === params.partId);
		const total = filtered.length;
		const offset = params?.offset ?? 0;
		const limit = params?.limit ?? total;
		const data: SurfaceReportSummary[] = filtered
			.slice(offset, offset + limit)
			.map(({ particleData: _, sizeClassEntries: __, ...summary }) => summary);
		return of({ data, total }).pipe(delay(this.latencyMs));
	}

	getById(id: string): Observable<SurfaceReport | null> {
		const report = this.reports.find((r) => r.id === id);
		return of(report ? { ...report } : null).pipe(delay(this.latencyMs));
	}

	getSummaryById(id: string): Observable<SurfaceReportSummary | null> {
		const report = this.reports.find((r) => r.id === id);
		if (!report) return of(null).pipe(delay(this.latencyMs));
		const { particleData: _, sizeClassEntries: __, ...summary } = report;
		return of({ ...summary }).pipe(delay(this.latencyMs));
	}

	getByIdWithSpecification(id: string): Observable<SurfaceReportWithSpecification | null> {
		const report = this.reports.find((r) => r.id === id);
		if (!report) return of(null).pipe(delay(this.latencyMs));
		if (!report.specificationId || !this.specRepo) {
			return of({ ...report, specification: null }).pipe(delay(this.latencyMs));
		}
		return this.specRepo.getSpecification(report.specificationId).pipe(
			map((specification: Specification) => ({ ...report, specification })),
			catchError(() => of({ ...report, specification: null })),
			delay(this.latencyMs),
		);
	}

	create(input: CreateSurfaceReportDto): Observable<SurfaceReport> {
		const newReport: SurfaceReport = {
			id: crypto.randomUUID(),
			rawReportId: input.rawReportId ?? null,
			measurementSystemVendor: input.measurementSystemVendor ?? "PartikelART",
			measurementSystemId: input.measurementSystemId ?? null,
			partId: input.partId ?? null,
			siteId: input.siteId ?? null,
			examiner: input.examiner ?? null,
			dateOfArrival: input.dateOfArrival ?? null,
			dateOfExtraction: input.dateOfExtraction ?? null,
			dateOfAnalysis: input.dateOfAnalysis ?? new Date(),
			partsOnFilter: input.partsOnFilter ?? null,
			componentSurfaceAreaCm2: input.componentSurfaceAreaCm2 ?? null,
			weightMg: input.weightMg ?? null,
			comment: input.comment ?? null,
			ioResult: input.ioResult ?? null,
			pdfBlobPath: input.pdfBlobPath ?? null,
			imagePolarizedBlobPath: input.imagePolarizedBlobPath ?? null,
			imageNonPolarizedBlobPath: input.imageNonPolarizedBlobPath ?? null,
			particleData: input.particleData ?? null,
			specificationId: input.specificationId ?? null,
			tags: input.tags ?? null,
			manuallyEdited: false,
			sizeClassEntries: [],
			importedParticles: [],
			createdAt: new Date(),
			updatedAt: new Date(),
		};
		newReport.sizeClassEntries = this.buildSizeClassEntries(newReport);
		this.reports.push(newReport);
		return of({ ...newReport }).pipe(delay(this.latencyMs));
	}

	/**
	 * Create a report imported from an external measurement system (e.g. a
	 * PicEd Cora ASCII export): size class entries come precomputed from the
	 * import instead of being derived from particleData.
	 */
	createImported(input: CreateSurfaceReportDto, sizeClassEntries: SizeClassBin[]): Observable<SurfaceReport> {
		return this.create(input).pipe(
			map((report) => {
				const stored = this.reports.find((r) => r.id === report.id);
				if (stored) stored.sizeClassEntries = sizeClassEntries.map((bin) => ({ ...bin }));
				return { ...report, sizeClassEntries: sizeClassEntries.map((bin) => ({ ...bin })) };
			}),
		);
	}

	/** Find a report by its external measurement-system id (import duplicate check). */
	findByMeasurementSystemId(measurementSystemId: string): SurfaceReport | null {
		return this.reports.find((r) => r.measurementSystemId === measurementSystemId) ?? null;
	}

	update(id: string, changes: UpdateSurfaceReportDto): Observable<SurfaceReport> {
		const idx = this.reports.findIndex((r) => r.id === id);
		if (idx === -1) return throwError(() => new Error(`Surface report ${id} not found`));
		const updated: SurfaceReport = {
			...this.reports[idx],
			...changes,
			sizeClassEntries: this.reports[idx].sizeClassEntries,
			updatedAt: new Date(),
		};
		if (changes.particleData) {
			updated.sizeClassEntries = this.buildSizeClassEntries(updated);
		}
		this.reports[idx] = updated;
		return of({ ...updated }).pipe(delay(this.latencyMs));
	}

	delete(id: string): Observable<void> {
		const idx = this.reports.findIndex((r) => r.id === id);
		if (idx === -1) return throwError(() => new Error(`Surface report ${id} not found`));
		this.reports.splice(idx, 1);
		return of(undefined).pipe(delay(this.latencyMs));
	}

	assignSpecification(id: string, specificationId: string): Observable<SurfaceReport> {
		return this.update(id, { specificationId });
	}

	removeSpecification(id: string): Observable<SurfaceReport> {
		return this.update(id, { specificationId: null });
	}

	private particle(id: number, material: Material, um: number): Particle {
		const diamPx = Math.round(um / MICRONS_PER_PIXEL);
		const r = Math.max(1, diamPx / 2);
		const cx = 400 + (id % 20) * 30;
		const cy = 300 + Math.floor(id / 20) * 30;
		const exterior: [Point2D, Point2D, Point2D, Point2D] = [
			new Point2D(cx - r, cy - r),
			new Point2D(cx + r, cy - r),
			new Point2D(cx + r, cy + r),
			new Point2D(cx - r, cy + r),
		];
		const measurements: ParticleMeasurements = {
			feretMax: { distance: diamPx, endpoints: [new Point2D(cx - r, cy), new Point2D(cx + r, cy)] },
			feretMin: { width: Math.round(r), angle: 0, furthestPoint: new Point2D(cx, cy + r) },
			inCircle: { radius: Math.round(r), center: new Point2D(cx, cy) },
			areaPx: Math.round(Math.PI * r * r),
			computedClass: material,
			// Fibers are curved: the stretched length exceeds the caliper distance.
			...(material === Material.FIBER ? { stretchedLengthPx: Math.round(diamPx * 1.2) } : {}),
		};
		return Particle.fromContours(exterior, [], measurements);
	}

	/**
	 * Particle data captured from a real analysis run — genuine contours, so
	 * anything that draws particles gets realistic shapes rather than squares.
	 *
	 * That capture was taken at high resolution and is shared by the demo
	 * reports that benefit most from looking real. Other reports use
	 * {@link buildParticleData} for synthetic but varied distributions.
	 */
	private capturedParticleData(id: string): ParticleCollection {
		const collection = DemoParticleCollectionRepository.load(id);
		if (!collection) throw new Error(`Unknown demo particle collection: ${id}`);
		return collection;
	}

	/**
	 * Creates a varied particle collection for a demo report.
	 * Different seed → different particle distribution for visual variety.
	 */
	private buildParticleData(seed: number): ParticleCollection {
		// Realistic declining distribution: many small particles, few large ones.
		// Larger sizes appear only for some seeds so reports differ visibly.
		const particleCounts = [
			{ material: Material.METAL, um: 5, count: 20 + (seed % 10) },
			{ material: Material.METAL, um: 15, count: 12 + (seed % 7) },
			{ material: Material.METAL, um: 30, count: 8 + (seed % 5) },
			{ material: Material.METAL, um: 70, count: 4 + (seed % 4) },
			{ material: Material.METAL, um: 160 + (seed % 5) * 10, count: 1 + (seed % 2) },
			{ material: Material.METAL, um: 420 + (seed % 7) * 30, count: seed % 3 === 0 ? 1 : 0 },
			{ material: Material.NON_METAL, um: 40, count: 10 + (seed % 6) },
			{ material: Material.NON_METAL, um: 75, count: 5 + (seed % 3) },
			{ material: Material.NON_METAL, um: 220 + (seed % 4) * 20, count: 2 + (seed % 3) },
			{ material: Material.NON_METAL, um: 520 + (seed % 5) * 50, count: seed % 2 },
			{ material: Material.NON_METAL, um: 1100 + (seed % 3) * 150, count: seed % 4 === 0 ? 1 : 0 },
			{ material: Material.FIBER, um: 350, count: 1 + (seed % 2) },
			{ material: Material.FIBER, um: 750 + (seed % 5) * 80, count: seed % 2 },
			{ material: Material.FIBER, um: 1600 + (seed % 4) * 200, count: seed % 3 === 0 ? 1 : 0 },
		];

		const particles: Particle[] = [];
		let id = 1;
		for (const { material, um, count } of particleCounts) {
			for (let i = 0; i < count; i++) {
				particles.push(this.particle(id++, material, um));
			}
		}

		return {
			type: "ParticleCollection",
			version: 1,
			crs: { type: "image", micronsPerPixel: MICRONS_PER_PIXEL, imageDimensions: [1200, 1200] },
			metadata: {
				analysisDate: new Date(Date.now() - seed * 86400000).toISOString(),
				coverage: 0.15 + (seed % 10) * 0.01,
				mode: "single",
				softwareVersion: "4.0.0",
			},
			particles,
		};
	}

	private buildSizeClassEntries(report: SurfaceReport): SurfaceReport["sizeClassEntries"] {
		if (!report.particleData) return [];
		const vdaClasses = VDA19_SIZE_CLASSES.map((sc) => toSizeClass(sc, WayOfMeasurement.FeretMax));
		return vdaClasses.map((sc) => createSizeClassBinFromSurfaceReport(report, sc));
	}

	// ── Seed data ──────────────────────────────────────────────────────────────

	private buildSeedReports(): SurfaceReport[] {
		const now = new Date();
		const daysAgo = (n: number) => new Date(now.getTime() - n * 86400000);

		const baseReport = (overrides: Partial<SurfaceReport> & { id: string }): SurfaceReport => ({
			rawReportId: null,
			measurementSystemVendor: "PartikelART",
			measurementSystemId: "MS-001",
			partId: PART_ALPHA,
			siteId: SITE_1,
			examiner: "Jane Doe",
			dateOfArrival: daysAgo(30),
			dateOfExtraction: daysAgo(29),
			dateOfAnalysis: daysAgo(28),
			partsOnFilter: 1,
			componentSurfaceAreaCm2: 50.0,
			weightMg: 2.5,
			comment: "",
			ioResult: null,
			pdfBlobPath: null,
			imagePolarizedBlobPath: null,
			imageNonPolarizedBlobPath: null,
			particleData: this.buildParticleData(1),
			specificationId: SPEC_ALPHA,
			manuallyEdited: false,
			sizeClassEntries: [],
			importedParticles: [],
			createdAt: daysAgo(28),
			updatedAt: daysAgo(28),
			...overrides,
		});

		return [
			// ── Site 1 — Part Alpha (Scheibe) ──
			baseReport({
				id: "demo-report-001",
				rawReportId: "123001",
				partId: PART_ALPHA,
				examiner: "Jane Doe",
				dateOfArrival: daysAgo(2),
				dateOfExtraction: daysAgo(1),
				dateOfAnalysis: daysAgo(1),
				componentSurfaceAreaCm2: 50.0,
				weightMg: 1.8,
				ioResult: true,
				specificationId: SPEC_ALPHA,
				comment: "Clean component, well within limits.",
				particleData: this.buildParticleData(1),
				createdAt: daysAgo(1),
				updatedAt: daysAgo(1),
			}),
			baseReport({
				id: "demo-report-002",
				rawReportId: "123002",
				partId: PART_ALPHA,
				examiner: "Max Mustermann",
				dateOfArrival: daysAgo(5),
				dateOfExtraction: daysAgo(4),
				dateOfAnalysis: daysAgo(4),
				componentSurfaceAreaCm2: 50.0,
				weightMg: 2.1,
				ioResult: false,
				specificationId: SPEC_ALPHA,
				comment: "Metallic particle exceeded 15µm threshold.",
				particleData: this.buildParticleData(2),
				createdAt: daysAgo(4),
				updatedAt: daysAgo(4),
			}),
			baseReport({
				id: "demo-report-003",
				rawReportId: "123003",
				partId: PART_ALPHA,
				examiner: "Jane Doe",
				dateOfArrival: daysAgo(10),
				dateOfExtraction: daysAgo(9),
				dateOfAnalysis: daysAgo(9),
				componentSurfaceAreaCm2: 50.0,
				weightMg: 1.5,
				ioResult: true,
				specificationId: SPEC_ALPHA,
				comment: "",
				particleData: this.buildParticleData(3),
				createdAt: daysAgo(9),
				updatedAt: daysAgo(9),
			}),

			// ── Site 1 — Part Beta (Blech) ──
			baseReport({
				id: "demo-report-004",
				rawReportId: "123004",
				partId: PART_BETA,
				examiner: "Max Mustermann",
				dateOfArrival: daysAgo(1),
				dateOfExtraction: daysAgo(1),
				dateOfAnalysis: daysAgo(0),
				componentSurfaceAreaCm2: 120.0,
				weightMg: 3.2,
				ioResult: null,
				specificationId: SPEC_BETA,
				comment: "Awaiting specification evaluation.",
				particleData: this.capturedParticleData("demo-clean"),
				createdAt: daysAgo(0),
				updatedAt: daysAgo(0),
			}),
			baseReport({
				id: "demo-report-005",
				rawReportId: "123005",
				partId: PART_BETA,
				examiner: "Jane Doe",
				dateOfArrival: daysAgo(7),
				dateOfExtraction: daysAgo(6),
				dateOfAnalysis: daysAgo(6),
				componentSurfaceAreaCm2: 120.0,
				weightMg: 2.8,
				ioResult: true,
				specificationId: SPEC_BETA,
				comment: "Clean.",
				particleData: this.capturedParticleData("demo-typical"),
				createdAt: daysAgo(6),
				updatedAt: daysAgo(6),
			}),
			baseReport({
				id: "demo-report-006",
				rawReportId: "123006",
				partId: PART_BETA,
				examiner: "Sabine Schmidt",
				dateOfArrival: daysAgo(14),
				dateOfExtraction: daysAgo(13),
				dateOfAnalysis: daysAgo(13),
				componentSurfaceAreaCm2: 120.0,
				weightMg: 4.0,
				ioResult: false,
				specificationId: SPEC_BETA,
				comment: "Non-metallic contamination above limit.",
				particleData: this.capturedParticleData("demo-contaminated"),
				createdAt: daysAgo(13),
				updatedAt: daysAgo(13),
			}),

			// ── Site 1 — Part Gamma (Flansch) ──
			baseReport({
				id: "demo-report-007",
				rawReportId: "123007",
				partId: PART_GAMMA,
				examiner: "Sabine Schmidt",
				dateOfArrival: daysAgo(3),
				dateOfExtraction: daysAgo(2),
				dateOfAnalysis: daysAgo(2),
				componentSurfaceAreaCm2: 300.0,
				weightMg: 5.5,
				ioResult: true,
				specificationId: SPEC_ALPHA,
				comment: "Excellent cleanliness.",
				particleData: this.buildParticleData(7),
				createdAt: daysAgo(2),
				updatedAt: daysAgo(2),
			}),
			baseReport({
				id: "demo-report-008",
				rawReportId: "123008",
				partId: PART_GAMMA,
				examiner: "Max Mustermann",
				dateOfArrival: daysAgo(12),
				dateOfExtraction: daysAgo(11),
				dateOfAnalysis: daysAgo(11),
				componentSurfaceAreaCm2: 300.0,
				weightMg: 6.2,
				ioResult: true,
				specificationId: SPEC_ALPHA,
				comment: "",
				particleData: this.buildParticleData(8),
				createdAt: daysAgo(11),
				updatedAt: daysAgo(11),
			}),

			// ── Site 1 — Part Delta (Getriebe) ──
			baseReport({
				id: "demo-report-009",
				rawReportId: "123009",
				partId: PART_DELTA,
				examiner: "Jane Doe",
				dateOfArrival: daysAgo(4),
				dateOfExtraction: daysAgo(3),
				dateOfAnalysis: daysAgo(3),
				componentSurfaceAreaCm2: 500.0,
				weightMg: 8.0,
				ioResult: false,
				specificationId: null,
				comment: "No specification assigned yet — manual review required.",
				particleData: this.buildParticleData(9),
				createdAt: daysAgo(3),
				updatedAt: daysAgo(3),
			}),
			baseReport({
				id: "demo-report-010",
				rawReportId: "123010",
				partId: PART_DELTA,
				examiner: "Sabine Schmidt",
				dateOfArrival: daysAgo(20),
				dateOfExtraction: daysAgo(19),
				dateOfAnalysis: daysAgo(19),
				componentSurfaceAreaCm2: 500.0,
				weightMg: 7.5,
				ioResult: true,
				specificationId: SPEC_ALPHA,
				comment: "",
				particleData: this.buildParticleData(10),
				createdAt: daysAgo(19),
				updatedAt: daysAgo(19),
			}),

			// ── Site 2 — Part Epsilon (Stecker) ──
			baseReport({
				id: "demo-report-011",
				rawReportId: "123011",
				partId: PART_EPSILON,
				siteId: SITE_2,
				examiner: "Pierre Dupont",
				dateOfArrival: daysAgo(6),
				dateOfExtraction: daysAgo(5),
				dateOfAnalysis: daysAgo(5),
				componentSurfaceAreaCm2: 25.0,
				weightMg: 1.0,
				ioResult: true,
				specificationId: SPEC_DELTA,
				comment: "Within spec.",
				particleData: this.buildParticleData(11),
				createdAt: daysAgo(5),
				updatedAt: daysAgo(5),
			}),
			baseReport({
				id: "demo-report-012",
				rawReportId: "123012",
				partId: PART_EPSILON,
				siteId: SITE_2,
				examiner: "Pierre Dupont",
				dateOfArrival: daysAgo(15),
				dateOfExtraction: daysAgo(14),
				dateOfAnalysis: daysAgo(14),
				componentSurfaceAreaCm2: 25.0,
				weightMg: 1.2,
				ioResult: true,
				specificationId: SPEC_DELTA,
				comment: "",
				particleData: this.buildParticleData(12),
				createdAt: daysAgo(14),
				updatedAt: daysAgo(14),
			}),

			// ── Site 2 — Part Zeta (Schiene) ──
			baseReport({
				id: "demo-report-013",
				rawReportId: "123013",
				partId: PART_ZETA,
				siteId: SITE_2,
				examiner: "Pierre Dupont",
				dateOfArrival: daysAgo(8),
				dateOfExtraction: daysAgo(7),
				dateOfAnalysis: daysAgo(7),
				componentSurfaceAreaCm2: 80.0,
				weightMg: 2.0,
				ioResult: false,
				specificationId: SPEC_DELTA,
				comment: "Fiber contamination detected above category limit.",
				particleData: this.buildParticleData(13),
				createdAt: daysAgo(7),
				updatedAt: daysAgo(7),
			}),
			baseReport({
				id: "demo-report-014",
				rawReportId: "123014",
				partId: PART_ZETA,
				siteId: SITE_2,
				examiner: "Marie Curie",
				dateOfArrival: daysAgo(25),
				dateOfExtraction: daysAgo(24),
				dateOfAnalysis: daysAgo(24),
				componentSurfaceAreaCm2: 80.0,
				weightMg: 1.7,
				ioResult: true,
				specificationId: SPEC_DELTA,
				comment: "Clean.",
				particleData: this.buildParticleData(14),
				createdAt: daysAgo(24),
				updatedAt: daysAgo(24),
			}),
		];
	}
}
